import React, { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";
import { loginUser } from "./LoginService";
import useForm from "../../hooks/useForm";
import UserContext from "../../Shared/UserContext";
import logo from "../../images/logo.png";
import SnackbarNotification from "../SnackbarNotification";
import EyeIcon from "../../icons/eye.svg";
import closeIcon from "../../icons/xW.svg";
import EyeOffIcon from "../../icons/eye-off.svg";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [banner, setBanner] = useState(location.state?.banner);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };
  useEffect(() => {
    const browserLang = navigator.language;
    console.log(`Browser language detected: ${browserLang}`, navigator);
    const shortLang = browserLang.split("-")[0]; 

    if (["en", "fr"].includes(shortLang)) {
      i18n.changeLanguage(shortLang);
    } else {
      i18n.changeLanguage("fr");
    }
  }, []);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const history = useHistory();
  const {
    dispatch,
    state: { status, errorMessage },
  } = useContext(UserContext);

  const { values, onChange, onSubmit } = useForm(
    { username: "", password: "" },
    (formData) => loginUser(dispatch, formData, history)
  );

  const handleLogin = async (event) => {
    event.preventDefault();
    if (!values.username || !values.password) {
      showSnackbar(t('placeholder.empty_fields'), 'error');
      return;
    }
    onSubmit();
  };

  useEffect(() => {
    if (status === "error") {
      console.error(errorMessage);
    }
  }, [status, errorMessage]);

  return (

    <div className="relative flex items-center justify-center h-screen bg-gray-100">
      <Helmet>
        <title>GrayFox - Log in</title>
      </Helmet>
      {/* Background Blur */}
      <div className="absolute top-0 left-0 w-full h-40 bg-blur opacity-20 blur-3xl"></div>

      {/* Background Image */}
      <div className="absolute bottom-0 left-0 h-full w-1/2 bg-cover bg-center"
        style={{ backgroundImage: `url(${require('../../images/background.png')})`, backgroundSize: "100%", backgroundPosition: "center" }}>
      </div>

      {banner && (
        <div className={`fixed top-8 right-4 px-6 py-3 font-semibold text-[18px] rounded-[12px] max-w-[550px] min-w-[300px] z-50 ${banner.type === 'success' ? 'bg-[#0AD69FC7] border-2 border-[#13C190] text-white' : 'bg-[#EF476F] text-white'}`}>
          <img
            src={closeIcon}
            alt="close icon"
            className="absolute top-2 right-2 w-4 h-4 cursor-pointer"
            onClick={() => setBanner(null)}
          />
          {banner.message}
        </div>
      )}

      <div className="relative w-[35%] z-10 text-center">
        <form onSubmit={handleLogin}>
          <img src={logo} alt="Logo" className="block mx-auto w-[250px] mb-8 mt-4" />
          <div className="text-left">
            <h2 className="text-[32px] font-semibold text-[#38485C] ">{t(`login.title`)}</h2>
            <p className="text-[#38485C] font-medium text-[18px] mt-2 mb-10">{t(`login.text`)}.</p>
            <label className="text-[18px] font-semibold text-[#38485C]" htmlFor="username">{t(`login.email`)}</label>
            <input
              type="text"
              id="username"
              name="username"
              value={values.username}
              onChange={onChange}
              placeholder={t(`login.email_placeholder`)}
              className="w-full px-4 py-3 placeholder-[16px] font-semibold placeholder-[#AFB0B2] border border-[#E3E3E3] rounded-lg mt-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="mt-4 text-left">
            <label className="text-[18px] font-semibold text-[#38485C]" htmlFor="password">{t(`login.password`)}</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={values.password}
                onChange={onChange}
                placeholder={t(`login.password`)}
                className={`w-full px-4 py-3 placeholder-[16px] font-semibold placeholder-[#AFB0B2] border border-[#E3E3E3] rounded-lg mt-1 focus:outline-none focus:ring-2 ${status === 'error' ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-indigo-500'}`}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-3 top-5 text-gray-500 text-sm"
              >
                <img src={showPassword ? EyeOffIcon : EyeIcon} alt="Toggle password visibility" className="w-5 h-5" />
              </button>
            </div>
          </div>

          {status === "error" && (
            <p className="text-red-500 text-sm mt-2 flex items-center">
              <span className="mr-1">⚠️</span> {t(`login.error_msg`)}.
            </p>
          )}
          <div className="flex gap-4 mt-10">
            <button
              type="button"
              onClick={() => history.push('/signup')}
              className="w-1/2 py-3 bg-[#ffffff] text-primary text-[18px] rounded-[12px] hover:bg-primary/10 transition font-semibold shadow-md"
            >
              {t(`login.signup`)}
            </button>
            <button
              type="submit"
              className="w-1/2 py-3 bg-primary text-white text-[18px] rounded-[12px] hover:bg-indigo-500 transition font-semibold shadow-md"
            >
              {t(`login.title`)}
            </button>
          </div>

        </form>
      </div>
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Login;
